import { useEffect } from "react";
import { observer } from "mobx-react-lite";
import { AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useStore } from "@/hooks/useStore";
import { useAsyncStateStore } from "@/hooks/useAsyncStateStore";
import { structureProperty } from "@/endpoints/dryp/structureProperty";

interface StructureConfigBoxProps {
	structureId: Dryp.StructureId;
	onConfigured?: () => void;
}

export const StructureConfigBox: React.FC<StructureConfigBoxProps> = observer(({ structureId, onConfigured }) => {
	const {
		uiStore: { modalStore },
	} = useStore();
	const propertyCountsApi = useAsyncStateStore(structureProperty.getCountsForStructure);

	useEffect(() => {
		propertyCountsApi.fetch(structureId);
	}, [structureId]);

	const missingCount = propertyCountsApi.data?.missingForConnectivityCount ?? 0;
	if (missingCount === 0) return null;

	const handleConfigured = () => {
		propertyCountsApi.fetch(structureId);
		onConfigured?.();
	};

	return (
		<div className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-700">
			<AlertCircle className="mt-px h-3.5 w-3.5 shrink-0 text-amber-500" />
			<div className="flex flex-col gap-1">
				<span>
					<span className="font-medium">Missing I&amp;I configuration.</span> {missingCount} propert
					{missingCount === 1 ? "y" : "ies"} needed to activate I&amp;I for this structure.
				</span>
				<Button
					variant="link"
					size="sm"
					className="h-auto self-start p-0 text-[11px] font-medium text-amber-800"
					onClick={() =>
						modalStore.open("structurePropertiesWizard", { structureId, onClose: handleConfigured })
					}
				>
					Add I&amp;I configuration
				</Button>
			</div>
		</div>
	);
});
